"use client";
import React from "react";
import {
  Box as ThemeUIBox,
  CSSPseudoSelectorProps,
  ThemeUICSSObject,
  ThemeUICSSProperties
} from "theme-ui";

import {
  InheritedStylesProvider,
  useMergeStyles
} from "../../hooks/useInheritedStyles";
import {
  resetClassName,
  ThemeBorders,
  ThemeColors,
  ThemeFonts,
  ThemeFontSizes,
  ThemeFontWeights,
  ThemeLetterSpaces,
  ThemeMargins,
  ThemeRadii,
  ThemeShadows,
  ThemeSizes,
  ThemeSpaces,
  ThemeString,
  ThemeTransitions,
  ThemeZIndices
} from "../Theme";
import { ThemeValue } from "../Theme/types";

export type ThemeUIBoxProps = React.ComponentProps<typeof ThemeUIBox>;

export interface BoxSxProps {
  "-webkit-box-orient"?: ThemeString;
  "-webkit-line-clamp"?: ThemeString;
  accentColor?: ThemeColors;
  background?: ThemeColors | ThemeUICSSProperties["background"];
  backgroundColor?: ThemeColors;
  bg?: ThemeColors | ThemeUICSSProperties["bg"];
  borderBottom?: ThemeBorders | ThemeColors;
  borderBottomColor?: ThemeColors;
  border?: ThemeBorders | ThemeColors;
  borderColor?: ThemeColors;
  borderLeft?: ThemeBorders | ThemeColors;
  borderLeftColor?: ThemeColors;
  borderBottomLeftRadius?: ThemeRadii;
  borderBottomRightRadius?: ThemeRadii;
  borderRadius?: ThemeRadii;
  borderTopLeftRadius?: ThemeRadii;
  borderTopRightRadius?: ThemeRadii;
  borderRight?: ThemeBorders | ThemeColors;
  borderRightColor?: ThemeColors;
  borderTop?: ThemeBorders | ThemeColors;
  borderTopColor?: ThemeColors;
  bottom?: ThemeSpaces;
  boxShadow?: ThemeShadows;
  caretColor?: ThemeColors;
  color?: ThemeColors;
  columnGap?: ThemeSpaces;
  columnRuleColor?: ThemeColors;
  columnWidth?: ThemeSizes;
  fill?: ThemeColors;
  flexBasis?: ThemeSizes;
  fontFamily?: ThemeFonts;
  fontSize?: ThemeFontSizes;
  fontWeight?: ThemeFontWeights;
  gap?: ThemeSpaces;
  gridColumnGap?: ThemeSpaces;
  gridGap?: ThemeSpaces;
  gridRowGap?: ThemeSpaces;
  height?: ThemeSizes;
  left?: ThemeSpaces;
  letterSpacing?: ThemeLetterSpaces;
  lineHeight?: ThemeFontSizes;
  m?: ThemeMargins;
  margin?: ThemeMargins;
  marginBlock?: ThemeMargins;
  marginBlockEnd?: ThemeMargins;
  marginBlockStart?: ThemeMargins;
  marginBottom?: ThemeMargins;
  marginInline?: ThemeMargins;
  marginInlineEnd?: ThemeMargins;
  marginInlineStart?: ThemeMargins;
  marginLeft?: ThemeMargins;
  marginRight?: ThemeMargins;
  marginTop?: ThemeMargins;
  maxHeight?: ThemeSizes;
  maxWidth?: ThemeSizes;
  mb?: ThemeMargins;
  minHeight?: ThemeSizes;
  minWidth?: ThemeSizes;
  ml?: ThemeMargins;
  mr?: ThemeMargins;
  mt?: ThemeMargins;
  mx?: ThemeMargins;
  my?: ThemeMargins;
  outlineColor?: ThemeColors;
  p?: ThemeSpaces;
  padding?: ThemeSpaces;
  paddingBlock?: ThemeSpaces;
  paddingBlockEnd?: ThemeSpaces;
  paddingBlockStart?: ThemeSpaces;
  paddingBottom?: ThemeSpaces;
  paddingInline?: ThemeSpaces;
  paddingInlineEnd?: ThemeSpaces;
  paddingInlineStart?: ThemeSpaces;
  paddingLeft?: ThemeSpaces;
  paddingRight?: ThemeSpaces;
  paddingTop?: ThemeSpaces;
  pb?: ThemeSpaces;
  pl?: ThemeSpaces;
  pr?: ThemeSpaces;
  pt?: ThemeSpaces;
  px?: ThemeSpaces;
  py?: ThemeSpaces;
  right?: ThemeSpaces;
  rowGap?: ThemeSpaces;
  size?: ThemeSizes;
  stroke?: ThemeColors;
  textDecorationColor?: ThemeColors;
  textShadow?: ThemeShadows;
  top?: ThemeSpaces;
  transition?: ThemeTransitions;
  transform?: ThemeValue<string>;
  width?: ThemeSizes;
  zIndex?: ThemeZIndices;
}

type BoxPsuedoSelectors = {
  [K in keyof CSSPseudoSelectorProps<unknown> as K extends keyof BoxSxProps
    ? never
    : K]?: BoxSxProps & Omit<ThemeUICSSProperties, keyof BoxSxProps>;
};

type BoxSxTyped = BoxSxProps & BoxPsuedoSelectors;

export type BoxSx = BoxSxTyped &
  Omit<ThemeUICSSProperties, keyof BoxSxTyped> &
  Omit<CSSPseudoSelectorProps<unknown>, keyof BoxSxTyped>;

interface BoxWithSxProps {
  sx?: BoxSx;
}

export interface BoxDiv
  extends Partial<React.HTMLAttributes<HTMLDivElement>>,
    BoxWithSxProps {
  as?: Omit<
    keyof JSX.IntrinsicElements,
    | "a"
    | "audio"
    | "button"
    | "canvas"
    | "form"
    | "iframe"
    | "img"
    | "input"
    | "label"
    | "select"
    | "svg"
    | "video"
  >;
}

export interface BoxAnchor
  extends Partial<React.AnchorHTMLAttributes<HTMLAnchorElement>>,
    BoxWithSxProps {
  as: "a";
}

export interface BoxAudio
  extends Partial<React.AudioHTMLAttributes<HTMLAudioElement>>,
    BoxWithSxProps {
  as: "audio";
}

export interface BoxButton
  extends Partial<React.ButtonHTMLAttributes<HTMLButtonElement>>,
    BoxWithSxProps {
  as: "button";
}

export interface BoxCanvas
  extends Partial<React.CanvasHTMLAttributes<HTMLCanvasElement>>,
    BoxWithSxProps {
  as: "canvas";
}

export interface BoxForm
  extends Partial<React.FormHTMLAttributes<HTMLFormElement>>,
    BoxWithSxProps {
  as: "form";
}

export interface BoxIframe
  extends Partial<React.IframeHTMLAttributes<HTMLIFrameElement>>,
    BoxWithSxProps {
  as: "iframe";
}

export interface BoxImg
  extends Partial<React.ImgHTMLAttributes<HTMLImageElement>>,
    BoxWithSxProps {
  as: "img";
}

export interface BoxInput
  extends Partial<React.InputHTMLAttributes<HTMLInputElement>>,
    BoxWithSxProps {
  as: "input";
}

export interface BoxLabel
  extends Partial<React.LabelHTMLAttributes<HTMLLabelElement>>,
    BoxWithSxProps {
  as: "label";
}

export interface BoxSelect
  extends Partial<React.SelectHTMLAttributes<HTMLSelectElement>>,
    BoxWithSxProps {
  as: "select";
}

export interface BoxSVG
  extends Partial<React.SVGProps<SVGElement>>,
    BoxWithSxProps {
  as: "svg";
}

export interface BoxVideo
  extends Partial<React.VideoHTMLAttributes<HTMLVideoElement>>,
    BoxWithSxProps {
  as: "video";
}

export type BoxProps =
  | BoxAnchor
  | BoxAudio
  | BoxButton
  | BoxCanvas
  | BoxDiv
  | BoxForm
  | BoxIframe
  | BoxImg
  | BoxInput
  | BoxLabel
  | BoxSelect
  | BoxSVG
  | BoxVideo;

const getClassNameWithReset = (className?: string): string =>
  resetClassName + (className ? ` ${className}` : "");

export const Box = React.forwardRef<HTMLElement, BoxProps>(
  ({ className, sx: sxInitial, ...props }, ref) => {
    const sx: BoxSx = {
      fontFamily: "body",
      ...sxInitial
    };
    const { newInheritedStylesContext, mergedSx } = useMergeStyles(sx);
    return (
      <InheritedStylesProvider styleContext={newInheritedStylesContext}>
        <ThemeUIBox
          className={getClassNameWithReset(className)}
          ref={ref}
          sx={mergedSx as ThemeUICSSObject}
          {...(props as unknown as React.ComponentProps<typeof ThemeUIBox>)}
        />
      </InheritedStylesProvider>
    );
  }
);

Box.displayName = "Box";
